import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { ActionsCard } from "@/components/orders/ActionsCard";
import { AgentCard } from "@/components/orders/AgentCard";
import { CustomerCard } from "@/components/orders/CustomerCard";
import { IdDocuments } from "@/components/orders/IdDocuments";
import { JobCard } from "@/components/orders/JobCard";
import { PaymentsCard } from "@/components/orders/PaymentsCard";
import { CrmInvoicesCard } from "@/components/orders/CrmInvoicesCard";
import { RoomsCard } from "@/components/orders/RoomsCard";
import { SummaryCard } from "@/components/orders/SummaryCard";
import { Timeline } from "@/components/orders/Timeline";
import { BTN_PRIMARY } from "@/components/orders/ui";
import { api, ApiError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import {
  cleanDraft,
  toDraft,
  type OrderFull,
  type OrderInput,
  type OrderSettingsBundle,
  type OrderTotals,
} from "@/lib/orders";

const EMPTY: OrderInput = {
  customerName: "",
  company: "",
  mobile: "",
  email: "",
  emirate: "Dubai",
  siteAddress: "",
  lat: null,
  lng: null,
  crmLeadId: null,
  assigneeId: null,
  rooms: [{ name: "Main area", kind: "custom", floor: null, wall: null }],
  bracketPct: null,
  discountType: null,
  discountValue: null,
  discountReason: "",
  surveyDate: null,
  installDate: null,
  notes: "",
};

/** `/orders/new` and `/orders/:id` — one page builds, edits and drives an order. */
export default function OrderEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [bundle, setBundle] = useState<OrderSettingsBundle | null>(null);
  const [order, setOrder] = useState<OrderFull | null>(null);
  const [draft, setDraft] = useState<OrderInput>(EMPTY);
  const [saved, setSaved] = useState<string>(JSON.stringify(EMPTY));
  const [totals, setTotals] = useState<OrderTotals | null>(null);
  const [pushToCrm, setPushToCrm] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const set = useCallback((patch: Partial<OrderInput>) => setDraft((d) => ({ ...d, ...patch })), []);

  const adopt = useCallback((o: OrderFull) => {
    setOrder(o);
    const d = toDraft(o);
    setDraft(d);
    setSaved(JSON.stringify(d));
  }, []);

  useEffect(() => {
    api.orderSettings().then(setBundle).catch(() => setError("Could not load order settings"));
  }, []);

  useEffect(() => {
    if (!id) {
      setOrder(null);
      setDraft(EMPTY);
      setSaved(JSON.stringify(EMPTY));
      return;
    }
    api.order(id).then(adopt).catch(() => setError("Order not found"));
  }, [id, adopt]);

  // Live totals: the server is the only place that knows the arithmetic.
  const quoteKey = JSON.stringify([draft.rooms, draft.bracketPct, draft.discountType, draft.discountValue]);
  const quoteSeq = useRef(0);
  useEffect(() => {
    const seq = ++quoteSeq.current;
    const handle = setTimeout(() => {
      const c = cleanDraft(draft);
      api
        .quoteOrder({ rooms: c.rooms, bracketPct: c.bracketPct, discountType: c.discountType, discountValue: c.discountValue })
        .then((t) => seq === quoteSeq.current && setTotals(t))
        .catch(() => undefined);
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey]);

  const dirty = useMemo(() => JSON.stringify(draft) !== saved, [draft, saved]);
  const editable = !order || order.editable;

  const reload = useCallback(async (o?: OrderFull) => {
    if (o) adopt(o);
    else if (id) adopt(await api.order(id));
  }, [id, adopt]);

  /** Photos changed: refresh the saved order without touching unsaved edits in the form. */
  const refreshOrder = useCallback(async () => {
    if (id) setOrder(await api.order(id));
  }, [id]);

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body = cleanDraft(draft);
      const result = order ? await api.updateOrder(order.id, body) : await api.createOrder(body);
      if (body.crmLeadId && pushToCrm) {
        const [firstname, ...rest] = body.customerName.trim().split(/\s+/);
        await api
          .updateCrmLead(body.crmLeadId, {
            firstname,
            lastname: rest.join(" ") || undefined,
            company: body.company,
            email: body.email,
            mobile: result.mobile ? `+${result.mobile}` : undefined,
          })
          .catch(() => setNotice("Saved. The CRM lead could not be updated."));
      }
      if (!order) navigate(`/orders/${result.id}`, { replace: true });
      else adopt(result);
      setNotice((n) => n ?? "Saved.");
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!bundle) {
    return (
      <AppShell>
        <div className="text-sm text-ice/60">{error ?? "Loading…"}</div>
      </AppShell>
    );
  }

  // A draft needs only a name. Mobile, site address and a section are checked when the order is sent or signed.
  const mobileDigits = draft.mobile.replace(/\D/g, "").length;
  // A reason is required only for a discount above the approval threshold; the page says what is missing.
  const overThreshold = (totals?.discountEffectivePct ?? 0) > bundle.settings.approvalDiscountPct;
  const blockers = [
    draft.customerName.trim().length < 2 && "the customer name",
    mobileDigits > 0 && mobileDigits < 8 && "a valid mobile number",
    overThreshold && !(draft.discountReason ?? "").trim() && `a reason for a discount above ${bundle.settings.approvalDiscountPct}%`,
  ].filter((x): x is string => Boolean(x));
  const canSave = editable && blockers.length === 0;
  const isDraft = !order || order.status === "draft" || order.status === "pending_approval";

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/orders" className="inline-flex items-center gap-1.5 text-xs text-ice/60 hover:text-paper">
            <ArrowLeft className="h-3.5 w-3.5" /> Orders
          </Link>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">
            {order ? `Order ${order.number}` : "New order"}
            {order && order.version > 1 && <span className="ml-2 text-base text-ice/50">v{order.version}</span>}
          </h1>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button type="button" className={BTN_PRIMARY} disabled={busy || !canSave || !dirty} onClick={save}>
            <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : !order ? "Save as draft" : isDraft ? "Save draft" : "Save changes"}
          </button>
          {dirty && editable && blockers.length > 0 && (
            <p className="text-xs text-goldbright">To save, add {blockers.join(" and ")}.</p>
          )}
        </div>
      </div>

      {!editable && order && (
        <p className="mt-3 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs text-ice/75">
          This order is {order.status}. Its content is locked — create a new revision to change it.
        </p>
      )}
      {error && <p role="alert" className="mt-3 rounded-lg border border-rose/60 bg-rose/15 px-3 py-2 text-sm text-pink">{error}</p>}
      {notice && <p className="mt-3 rounded-lg border border-green/50 bg-green/10 px-3 py-2 text-sm text-green">{notice}</p>}

      <div className="mt-2 grid gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <div>
          <CustomerCard draft={draft} set={set} disabled={!editable} pushToCrm={pushToCrm} setPushToCrm={setPushToCrm} />
          <RoomsCard
            rooms={draft.rooms}
            onChange={(rooms) => set({ rooms })}
            priceList={bundle.priceList}
            extraOptions={bundle.extraOptions}
            settings={bundle.settings}
            totals={totals}
            disabled={!editable}
            orderId={order?.id ?? null}
            savedRooms={order?.rooms}
            onPhotosChanged={refreshOrder}
          />
          <JobCard draft={draft} set={set} totals={totals} settings={bundle.settings} disabled={!editable} isAdmin={isAdmin} />
          {order && <IdDocuments order={order} onChanged={adopt} />}
          {order && <Timeline events={order.events} />}
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <SummaryCard order={order} totals={totals} settings={bundle.settings} />
          <AgentCard order={order} value={draft.assigneeId} dirty={dirty} onPick={(assigneeId) => set({ assigneeId })} onChanged={adopt} />
          {order && <ActionsCard order={order} isAdmin={isAdmin} dirty={dirty} onChanged={reload} />}
          {order && <PaymentsCard order={order} stripeConfigured={bundle.settings.stripe?.configured ?? false} onChanged={adopt} />}
          {order && (order.status === "signed" || order.crmInvoices.length > 0) && <CrmInvoicesCard order={order} onChanged={adopt} />}
        </aside>
      </div>
    </AppShell>
  );
}

function readError(e: unknown): string {
  if (!(e instanceof ApiError)) return "Could not save. Check the fields.";
  try {
    const parsed = JSON.parse(e.message) as { message?: string | string[] };
    const m = Array.isArray(parsed.message) ? parsed.message.join(", ") : parsed.message;
    if (m === "mobile") return "Please enter a valid mobile number, e.g. +971 50 123 4567.";
    return m ?? "Could not save.";
  } catch {
    return "Could not save.";
  }
}
